
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Project {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export const useProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Load projects (only when authenticated)
  const loadProjects = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      // If the user is not signed in, don't call the function (it will 401).
      if (!accessToken) {
        setProjects([]);
        return;
      }

      const { data, error } = await supabase.functions.invoke('projects-api', {
        method: 'GET',
      });

      if (error) {
        console.error('Error loading projects:', error);
        toast.error('Failed to load projects');
        return;
      }

      setProjects(data?.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create a new project (only when authenticated)
  const createProject = useCallback(async (name: string, description?: string) => {
    setIsCreating(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (!accessToken) {
        toast.error('Please sign in to create a project');
        return null;
      }

      const { data, error } = await supabase.functions.invoke('projects-api', {
        method: 'POST',
        body: { name, description },
      });

      if (error) {
        console.error('Error creating project:', error);
        toast.error('Failed to create project');
        return null;
      }

      const newProject = data.project as Project;
      setProjects((prev) => [newProject, ...prev]);
      toast.success(`Project "${name}" created successfully!`);
      return newProject;
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
      return null;
    } finally {
      setIsCreating(false);
    }
  }, []);

  // Load projects on mount and whenever auth state changes
  useEffect(() => {
    loadProjects();

    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.access_token) {
        setTimeout(() => loadProjects(), 0);
      } else {
        setProjects([]);
      }
    });

    return () => {
      data.subscription.unsubscribe();
    };
  }, [loadProjects]);

  // Set up real-time subscription for new projects (only when authenticated)
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let isMounted = true;

    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;

      if (!isMounted || !userId) return;

      channel = supabase
        .channel(`projects-changes-${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'projects',
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            const newProject = payload.new as Project;
            setProjects((prev) => {
              // Check if project already exists (to avoid duplicates)
              if (prev.some((p) => p.id === newProject.id)) return prev;
              return [newProject, ...prev];
            });
          }
        )
        .subscribe();
    })();

    return () => {
      isMounted = false;
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  return {
    projects,
    isLoading,
    isCreating,
    loadProjects,
    refetch: loadProjects,
    createProject,
  };
};

